import Spinner from "components/Spinner";
import { CollectorConfig } from "providers/HostedScrapingProvider/types";
import { followRedirectToDisableFollowRedirect } from "providers/HostedScrapingProvider/negateFollowRedirects";
import { formatCreditsWithCommaSeparators } from "utils";
import { preCalculatedRuntimeEstimation } from "./ProjectSummarySidebarHostedScraper";
import { Link } from "react-router-dom";
import { BillingPath } from "v2/pages/billing/BillingPath";
import { InfiniteProgressBar } from "components/InfiniteProgressBar";


type ISummaryRowProps = {
  title: string;
  value: string;
};

export const Separator = () => (
  <div className="w-full border-t border-slate-200 mt-4 mb-4" />
)

export const SummaryRow = ({title, value}: ISummaryRowProps) => {
  return (<div className="flex flex-row flew-wrap">
    <div className="font-semibold pr-2 whitespace-nowrap">{title}</div>
    <div className="overflow-hidden text-ellipsis">{value}</div>
  </div>);
}

export const textsHS = {
  costRow: {
    costPerJob: 'Cost per job:',
  },
  content: (creditsLeft: number, runsLeft: string | number, pluralS: 's'|'') => `You have ${creditsLeft} credits left in this billing period. With those remaining credits you would be able to run this project ${runsLeft} time${pluralS}.`
}

export const textsAP = {
  costRow: {
    costPerJob: 'Cost per request:',
  },
  content: (creditsLeft: number, runsLeft: string | number, pluralS: 's'|'') => `You have ${creditsLeft} credits left in this billing period. With those remaining credits you would be able to make ${runsLeft} request${pluralS} this month.`
}

type SideBarTexts = typeof textsHS;

export const CostRow = ({cost, costInProgress, texts}: {cost: number | undefined, costInProgress: boolean, texts: SideBarTexts}) => {
  const pluralS = cost === 1 ? '' : 's';
  return (<div className="flex flex-row flex-wrap items-center justify-between h-10 ">
    <div className="font-semibold pr-2">{texts.costRow.costPerJob}</div>
    {costInProgress ? <Spinner className="w-6 h-6 text-neutral-500 dark:text-neutral-500 animate-spin" />
      : <div><span data-testid="testEstimatedCost" className="text-semibold text-4xl">{cost ?? '-'}</span> <span className="text-sm">credit{pluralS}</span></div>
    }
  </div>);
};

export const CostRowHostedScraper = ({
  cost,
  costInProgress,
  runsLeft,
}: {
  cost: number | undefined;
  costInProgress: boolean;
  runsLeft?: string | number;
}) => {
  const creditPluralS = cost === 1 ? '' : 's';
  const runPluralS = runsLeft === 1 ? '' : 's';

  return (
    <div className="flex flex-col gap-2">
        {costInProgress ? (
          <div className="h-12 py-3">
            <Spinner className="w-6 h-6 text-neutral-500 dark:text-neutral-500 animate-spin" />
          </div>
        ) : (
          <div className="flex flex-row items-end gap-1.5">
            <span data-testid="testEstimatedCost" className="text-5xl leading-none text-neutral-900">
              {formatCreditsWithCommaSeparators(cost) ?? '-'}
            </span>
            <span className="text-base text-neutral-500 pb-1">credit{creditPluralS}</span>
          </div>
        )}
      {runsLeft !== undefined && (
        <p className="text-xs text-neutral-500">
          With your remaining credits you would be able to run this project {runsLeft} time{runPluralS}.
        </p>
      )}
      <p className="text-xs text-neutral-500">
        Need more credits?{' '}
        <Link to={BillingPath} className="text-primary-600">Upgrade plan</Link>
      </p>
    </div>
  );
};

export const PROGRESS_BAR_COLOR_ERROR = '#e51515';
export const PROGRESS_BAR_COLOR_WARNING = '#e6940e';
export const PROGRESS_BAR_COLOR_OK = '#1a22e4';

export const LittleProgressBar = ({ progress, color, label }: { progress: number; color: string; label?: string }) => {
  const clamped = Math.min(100, Math.max(0, progress));
  return (
    <div className="flex flex-col gap-1">
      {label && <div className="text-sm text-neutral-900">{label}</div>}
      <div className="w-full h-2 overflow-hidden bg-neutral-100">
        <div className="h-2" style={{ width: `${clamped}%`, backgroundColor: color }} />
      </div>
    </div>
  );
};

export const Credits = ({
  creditsLeft,
  creditsUsed,
  creditsNeededForThisJob,
}: {
  creditsLeft: number;
  creditsUsed: number;
  creditsNeededForThisJob: number;
}) => {
  const notEnoughCredits = creditsNeededForThisJob > 0 && creditsLeft < creditsNeededForThisJob;
  let progress = 0;
  if (creditsUsed + creditsLeft > 0) {
    progress = creditsUsed / (creditsUsed + creditsLeft) * 100;
  }

  const color = progress >= 90 ? PROGRESS_BAR_COLOR_ERROR : progress >= 70 ? PROGRESS_BAR_COLOR_WARNING : PROGRESS_BAR_COLOR_OK;

  return (
    <div className="flex flex-col gap-3">
      <LittleProgressBar
        progress={progress}
        color={color}
        label={`${formatCreditsWithCommaSeparators(creditsUsed)} used`}
      />
      {notEnoughCredits && (
        <div className="flex flex-col gap-3 p-4 bg-warning-50 border border-warning-200 rounded">
          <p className="text-sm text-neutral-900">
            This job will be created, but it will not run. You don't have enough credits to run it. Upgrade your plan or enable Pay-as-you-go to continue.
          </p>
          <Link to={BillingPath}>
            <button className="w-full h-8 bg-primary-600 text-white text-xs uppercase tracking-wide rounded px-3">
              Upgrade to a Higher Plan
            </button>
          </Link>
        </div>
      )}
    </div>
  );
};

export const SidebarConcurrency = ({
  concurrency,
  concurrencyLimit,
  inputCount,
  showWarning,
  concurrencyIsLoading,
}: {
  concurrency: number;
  concurrencyLimit: number;
  inputCount?: number;
  showWarning?: boolean;
  concurrencyIsLoading: boolean;
}) => {
  const progress = concurrencyLimit > 0 ? (concurrency / concurrencyLimit) * 100 : 0;
  const color = showWarning? PROGRESS_BAR_COLOR_WARNING : PROGRESS_BAR_COLOR_OK;
  const concurrencyLeft = concurrencyLimit - concurrency;
  const estimatedRunTime = inputCount === undefined ? undefined : preCalculatedRuntimeEstimation(inputCount, Math.max(concurrencyLeft, 0) );
  const progressBarLabel = `${concurrency} / ${concurrencyLimit} threads active`;
  return (
    <div className="flex flex-col gap-4">

      {!concurrencyIsLoading &&
        <LittleProgressBar progress={progress} color={color} label={progressBarLabel} />
      }

      {concurrencyIsLoading &&
        <InfiniteProgressBar/>
      }


      {estimatedRunTime && (
        <div className="flex flex-row justify-between text-sm text-neutral-900">
          <span>Estimated run time</span>
          <span className="font-medium">{estimatedRunTime}</span>
        </div>
      )}

      {showWarning && (
        <div className="flex flex-col gap-3 p-4 bg-warning-50 border border-warning-200 rounded">
          <p className="text-sm text-neutral-900">
            This job may run slow at your current concurrency.
          </p>
          <p className="text-xs text-neutral-500">
            Running other jobs will share concurrency and may increase the time.
          </p>
          <Link to={BillingPath}>
            <button className="w-full h-8 bg-primary-600 text-white text-xs uppercase tracking-wide rounded px-3">
              Upgrade for Faster Runs
            </button>
          </Link>
        </div>
      )}
    </div>
  );
};

export const UrlProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  return (<>
    {collectorConfig.apiParams?.premium && <SummaryRow title="Premium Ips" value="On" />}
    {collectorConfig.apiParams?.ultraPremium && <SummaryRow title="Advanced bypass mechanism" value="On" />}
    {followRedirectToDisableFollowRedirect(collectorConfig.apiParams?.followRedirect as boolean | undefined) && <SummaryRow title="Disable follow redirects" value="On" />}
    {collectorConfig.apiParams?.render && <SummaryRow title="JS Rendering" value="On" />}
    {collectorConfig.apiParams?.waitForSelector && <SummaryRow title="Wait for selector" value={collectorConfig.apiParams?.waitForSelector as string} />}
    {collectorConfig.apiParams?.retry404 && <SummaryRow title="Retry 404" value="On" />}
    {collectorConfig.apiParams?.autoparse  && <SummaryRow title="Auto parse"  value="On" />}
    {collectorConfig.apiParams?.countryCode && <SummaryRow title="Country Code" value={collectorConfig.apiParams?.countryCode as string} />}
    {collectorConfig.apiParams?.deviceType && <SummaryRow title="Device Type" value={collectorConfig.apiParams?.deviceType as string} />}
    {collectorConfig.apiParams?.sessionNumber && <SummaryRow title="Session number" value={String(collectorConfig.apiParams?.sessionNumber)} />}
    {collectorConfig.apiParams?.maxCost && <SummaryRow title="Max Cost" value={String(collectorConfig.apiParams?.maxCost)} />}
    {collectorConfig.apiParams?.binaryTarget && <SummaryRow title="Binary target" value="On" />}
    {collectorConfig.apiParams?.keepHeaders && <SummaryRow title="Keep headers" value="On" />}
    {collectorConfig.apiParams?.outputFormat && <SummaryRow title="Output format" value={String(collectorConfig.apiParams.outputFormat)} />}
  </>);
}

export const AmazonProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  return (<>
    {collectorConfig.apiParams?.countryCode && <SummaryRow title="Country Code" value={collectorConfig?.apiParams?.countryCode as string} />}
    {collectorConfig.apiParams?.tld && <SummaryRow title="Domain" value={collectorConfig?.apiParams?.tld as string} />}
  </>);
}

export const GoogleProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  return (<>
    {collectorConfig.apiParams?.countryCode && <SummaryRow title="Country Code" value={collectorConfig?.apiParams?.countryCode as string} />}
    {collectorConfig.apiParams?.tld && <SummaryRow title="Domain" value={collectorConfig?.apiParams?.tld as string} />}
    {collectorConfig.apiParams?.tbs && <SummaryRow title="Time based search" value={collectorConfig?.apiParams?.tbs as string} />}
    {collectorConfig.apiParams?.uule && <SummaryRow title="UULE" value={collectorConfig?.apiParams?.uule as string} />}
    {collectorConfig.apiParams?.additionalParams && <SummaryRow title="Additional parameters" value="On" />}
    {collectorConfig.apiParams?.latitude && <SummaryRow title="Latitude" value={collectorConfig?.apiParams?.latitude as string} />}
    {collectorConfig.apiParams?.longitude && <SummaryRow title="Longitude" value={collectorConfig?.apiParams?.longitude as string} />}
  </>);
}

export const WalmartProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  return (<>
    {collectorConfig.apiParams?.page && <SummaryRow title="Page" value={String(collectorConfig?.apiParams?.page)} />}
    {collectorConfig.apiParams?.tld && <SummaryRow title="Domain" value={collectorConfig?.apiParams?.tld as string} />}
    {collectorConfig.apiParams?.sort && <SummaryRow title="Sort by" value={collectorConfig?.apiParams?.sort as string} />}
  </>);
}

export const EbayProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  return (<>
    {collectorConfig.apiParams?.tld && <SummaryRow title="Domain" value={collectorConfig?.apiParams?.tld as string} />}
    {collectorConfig.apiParams?.countryCode && <SummaryRow title="Country code" value={collectorConfig?.apiParams?.countryCode as string} />}
    {collectorConfig.apiParams?.page && <SummaryRow  title="Page" value={collectorConfig.apiParams?.page as string} />}
    {collectorConfig.apiParams?.items_per_page && <SummaryRow  title="Items per page" value={collectorConfig.apiParams?.items_per_page as string} />}
    {collectorConfig.apiParams?.seller_id && <SummaryRow  title="Seller ID" value={collectorConfig.apiParams?.seller_id as string} />}
    {collectorConfig.apiParams?.condition && <SummaryRow  title="Condition" value={collectorConfig.apiParams?.condition as string} />}
    {collectorConfig.apiParams?.buying_format && <SummaryRow  title="Buying format" value={collectorConfig.apiParams?.buying_format as string} />}
    {collectorConfig.apiParams?.show_only && <SummaryRow  title="Show only" value={collectorConfig.apiParams?.show_only as string} />}
    {collectorConfig.apiParams?.sort_by && <SummaryRow  title="Sort by" value={collectorConfig.apiParams?.sort_by as string} />}
  </>);
}

export const HomeDepotProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  const url = (collectorConfig.apiParams?.url as string | undefined)?.trim();
  // A url is the active input and overrides the filters, so show it instead of the ignored values.
  if (url) {
    return <SummaryRow title="URL" value={url} />;
  }
  return (<>
    {collectorConfig.apiParams?.sortBy && <SummaryRow title="Sort by" value={collectorConfig?.apiParams?.sortBy as string} />}
    {collectorConfig.apiParams?.minPrice && <SummaryRow title="Min price" value={collectorConfig?.apiParams?.minPrice as string} />}
    {collectorConfig.apiParams?.maxPrice && <SummaryRow title="Max price" value={collectorConfig?.apiParams?.maxPrice as string} />}
    {collectorConfig.apiParams?.page && <SummaryRow title="Page" value={collectorConfig?.apiParams?.page as string} />}
  </>);
}

export const HomeDepotProductProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  const url = (collectorConfig.apiParams?.url as string | undefined)?.trim();
  const gtin = (collectorConfig.apiParams?.gtin as string | undefined)?.trim();
  return (<>
    {url && <SummaryRow title="URL" value={url} />}
    {gtin && <SummaryRow title="GTIN" value={gtin} />}
  </>);
}

export const HomeDepotReviewsProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  const url = (collectorConfig.apiParams?.url as string | undefined)?.trim();
  const gtin = (collectorConfig.apiParams?.gtin as string | undefined)?.trim();
  return (<>
    {url && <SummaryRow title="URL" value={url} />}
    {gtin && <SummaryRow title="GTIN" value={gtin} />}
    {collectorConfig.apiParams?.page && <SummaryRow title="Page" value={collectorConfig?.apiParams?.page as string} />}
  </>);
}

export const RedfinProjectDetails = ({collectorConfig}: {collectorConfig: CollectorConfig}) => {
  return (<>
    {collectorConfig.apiParams?.raw && <SummaryRow title="Raw data" value="On" />}
    {collectorConfig.apiParams?.tld && <SummaryRow title="Domain" value={collectorConfig?.apiParams?.tld as string} />}
    {collectorConfig.apiParams?.countryCode && <SummaryRow title="Country code" value={collectorConfig?.apiParams?.countryCode as string} />}
  </>);
}
